import clsx from "clsx";

import { BugTicketComment, BugTicketStatus } from "../redux/reducers/bugTicketSlice";
import { BUG_TICKET_STATUS_LABELS } from "./BugTicketStatusBadge";
import { Text, View } from "./base";
import { Icon } from "./base/Icon";
import { formatRelative } from "../utils/adminFormat";

const statusLabel = (status?: BugTicketStatus) => (status ? BUG_TICKET_STATUS_LABELS[status] : "Unknown");

type BugTicketTimelineProps = {
  comments: BugTicketComment[];
  /** How to label non-staff reply authors ("You" on the user page, the submitter's email on the admin page). */
  submitterLabel: string;
};

export const BugTicketTimeline = ({ comments, submitterLabel }: BugTicketTimelineProps) => {
  if (comments.length === 0) {
    return <Text className="block py-4 text-center text-sm text-gray-400 dark:text-gray-500">No replies yet.</Text>;
  }

  return (
    <View className="flex flex-col gap-3">
      {comments.map((comment) =>
        comment.type === "status_change" ? (
          <View
            key={comment.id}
            className="flex flex-row items-center justify-center gap-1.5 py-1"
          >
            <Icon
              name="refresh"
              size={12}
              className="text-gray-400 dark:text-gray-500"
            />
            <Text className="text-xs text-gray-400 dark:text-gray-500">
              Status changed from {statusLabel(comment.metadata?.from)} to {statusLabel(comment.metadata?.to)} · {formatRelative(String(comment.createdAt))}
            </Text>
          </View>
        ) : (
          <View
            key={comment.id}
            className={clsx(
              "rounded-lg border p-3",
              comment.isInternal
                ? "border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/20"
                : comment.authorIsStaff
                  ? "border-brand/30 bg-brand/5 dark:border-brand/40 dark:bg-brand/10"
                  : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50",
            )}
          >
            <View className="mb-1.5 flex flex-row items-center gap-2">
              <Icon
                name={comment.authorIsStaff ? "support_agent" : "person"}
                size={14}
                className={comment.authorIsStaff ? "text-brand" : "text-gray-400 dark:text-gray-500"}
              />
              <Text className="text-xs font-medium text-gray-700 dark:text-gray-300">{comment.authorIsStaff ? "VoiceRun Support" : submitterLabel}</Text>
              {comment.isInternal && (
                <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-900/60 dark:text-amber-300">
                  Internal
                </span>
              )}
              <Text className="ml-auto text-xs text-gray-400 dark:text-gray-500">{formatRelative(String(comment.createdAt))}</Text>
            </View>
            <Text className="block whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">{comment.body}</Text>
          </View>
        ),
      )}
    </View>
  );
};
